import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Wallet, PiggyBank, TrendingUp, Receipt, ArrowRight, Upload, Link2, Sparkles, Calculator, HelpCircle, FileText, BarChart3 } from 'lucide-react';
import GlassPanel from '../../components/GlassPanel';
import { AIExplanationBox, AIPanel, AIButton } from '../../components/AIComponents';
import { DataFreshnessPanel } from '../../components/DataFreshnessComponents';

const financeSubNav = [
  { path: '/finance', label: 'Overview', icon: Wallet },
  { path: '/finance/pots', label: 'Money Pots', icon: PiggyBank },
  { path: '/finance/cashflow', label: 'Cashflow', icon: TrendingUp },
  { path: '/finance/invoices', label: 'Invoices', icon: Receipt },
  { path: '/finance/margin', label: 'Margin', icon: BarChart3 },
  { path: '/finance/documents', label: 'Documents', icon: FileText },
  { path: '/finance/xero', label: 'Xero', icon: Link2 },
];

const mockPots = [
  { id: 1, type: 'OPERATING', balance: 4250.00, description: 'Day-to-day business expenses' },
  { id: 2, type: 'TAX', balance: 2840.00, description: 'Reserved for tax payments' },
  { id: 3, type: 'VAT', balance: 1920.00, description: 'VAT collected for HMRC' },
  { id: 4, type: 'SAVINGS', balance: 3500.00, description: 'Business savings' },
];

export function Finance() {
  const location = useLocation();

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1>Finance Life</h1>
          <p>Manage your business finances</p>
        </div>
      </div>

      {/* Sub Navigation */}
      <div className="flex gap-sm mb-xl" style={{ flexWrap: 'wrap' }}>
        {financeSubNav.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`btn ${location.pathname === item.path ? 'btn-primary' : 'btn-secondary'}`}
            style={{ gap: 'var(--space-sm)' }}
          >
            <item.icon size={16} />
            {item.label}
          </Link>
        ))}
      </div>

      {/* Data Freshness Panel */}
      <DataFreshnessPanel showRefresh />

      {/* AI Finance Assistant */}
      <AIPanel 
        title="AI Finance Assistant" 
        description="AI translates financial data into plain English guidance."
        className="mb-xl"
      >
        <div className="ai-actions-grid">
          <AIButton icon={Sparkles} tooltip="AI generation will be enabled soon">
            Explain my cashflow
          </AIButton>
          <AIButton icon={Calculator} tooltip="AI generation will be enabled soon">
            Can I afford this job?
          </AIButton>
          <AIButton icon={HelpCircle} tooltip="AI generation will be enabled soon">
            Why is my safe-to-spend low?
          </AIButton>
        </div>
      </AIPanel>

      {/* Safe to Spend Card */}
      <GlassPanel className="mb-lg">
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          padding: 'var(--space-md) 0'
        }}>
          <div>
            <div style={{ 
              fontSize: '0.75rem', 
              color: 'var(--text-muted)', 
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              marginBottom: 'var(--space-xs)'
            }}>
              Safe to Spend
            </div>
            <div style={{ 
              fontSize: '2.5rem', 
              fontWeight: '700', 
              color: 'var(--status-success)',
              fontFamily: 'var(--font-family-display)'
            }}>
              £4,250.00
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: 'var(--space-xs)' }}>
              After reserving for tax, VAT and commitments
            </div>
          </div>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 'var(--space-sm)',
            textAlign: 'right'
          }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Total Balance</div>
              <div style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--accent-primary)' }}>£12,510.00</div>
            </div>
          </div>
        </div>
      </GlassPanel>

      {/* Quick Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-md)', marginBottom: 'var(--space-xl)' }}>
        {mockPots.map((pot) => (
          <div key={pot.id} className="pot-card">
            <div className="pot-type">{pot.type}</div>
            <div className="pot-balance">£{pot.balance.toFixed(2)}</div>
            <div className="pot-description">{pot.description}</div>
          </div>
        ))}
      </div>

      {/* Quick Links */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-md)' }}>
        <Link to="/finance/pots" className="widget-card" style={{ textDecoration: 'none' }}>
          <div className="widget-header">
            <div className="widget-icon">
              <PiggyBank size={20} />
            </div>
          </div>
          <div className="widget-label">Manage Pots</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginTop: 'var(--space-sm)', color: 'var(--accent-primary)' }}>
            <span style={{ fontSize: '0.875rem' }}>View all pots</span>
            <ArrowRight size={14} />
          </div>
        </Link>

        <Link to="/finance/cashflow" className="widget-card" style={{ textDecoration: 'none' }}>
          <div className="widget-header">
            <div className="widget-icon">
              <TrendingUp size={20} />
            </div>
          </div>
          <div className="widget-label">Cashflow Timeline</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginTop: 'var(--space-sm)', color: 'var(--accent-primary)' }}>
            <span style={{ fontSize: '0.875rem' }}>View timeline</span>
            <ArrowRight size={14} />
          </div>
        </Link>

        <Link to="/finance/suppliers" className="widget-card" style={{ textDecoration: 'none' }}>
          <div className="widget-header">
            <div className="widget-icon">
              <Upload size={20} />
            </div>
          </div>
          <div className="widget-label">Supplier Upload</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginTop: 'var(--space-sm)', color: 'var(--accent-primary)' }}>
            <span style={{ fontSize: '0.875rem' }}>Upload invoices</span>
            <ArrowRight size={14} />
          </div>
        </Link>
      </div>

      <AIExplanationBox>
        AI helps you understand your finances in plain language. Ask questions about cashflow, affordability, and spending patterns.
      </AIExplanationBox>
    </div>
  );
}

export default Finance;
